import { create } from 'zustand';
import type { User } from '../../../shared/types/index.js';
import { useNotificationStore } from '../../notifications/store/useNotificationStore.js';
import { safeLocalStorage } from '../../../shared/utils/safeLocalStorage.js';

interface AuthState {
  token: string | null;
  user: User | null;
  isLoading: boolean;
  isLoginModalOpen: boolean;
  isRegisterModalOpen: boolean;
  openLoginModal: () => void;
  closeLoginModal: () => void;
  openRegisterModal: () => void;
  closeRegisterModal: () => void;
  setAuth: (user: User, token: string) => void;
  updateUser: (updatedUser: User, token?: string) => void;
  logout: () => void;
  loadInitialAuth: () => Promise<void>;
  switchAccount: (account: User) => void;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  token: safeLocalStorage.getItem('uibn_token'),
  user: (() => {
    try {
      const stored = safeLocalStorage.getItem('uibn_user');
      return stored && stored !== 'undefined' ? JSON.parse(stored) : null;
    } catch (e) {
      console.warn('[AuthStore] Failed to parse stored user:', e);
      try {
        safeLocalStorage.removeItem('uibn_user');
        safeLocalStorage.removeItem('uibn_token');
      } catch {}
      return null;
    }
  })(),
  isLoading: false,
  isLoginModalOpen: false,
  isRegisterModalOpen: false,

  openLoginModal: () => set({ isLoginModalOpen: true, isRegisterModalOpen: false }),
  closeLoginModal: () => set({ isLoginModalOpen: false }),
  openRegisterModal: () => {
    const user = get().user;
    if (user) {
      useNotificationStore.getState().showToast(
        'Sudah Terdaftar',
        `Anda sudah terdaftar dan masuk sebagai ${user.name}!`,
        'info'
      );
      return;
    }
    set({ isRegisterModalOpen: true, isLoginModalOpen: false });
  },
  closeRegisterModal: () => set({ isRegisterModalOpen: false }),

  setAuth: (user: User, token: string) => {
    safeLocalStorage.setItem('uibn_token', token);
    safeLocalStorage.setItem('uibn_user', JSON.stringify(user));
    set({ user, token });
  },

  updateUser: (updatedUser: User, token?: string) => {
    safeLocalStorage.setItem('uibn_user', JSON.stringify(updatedUser));
    if (token) {
      safeLocalStorage.setItem('uibn_token', token);
      set({ user: updatedUser, token });
    } else {
      set({ user: updatedUser });
    }
  },

  logout: () => {
    safeLocalStorage.removeItem('uibn_token');
    safeLocalStorage.removeItem('uibn_user');
    set({ user: null, token: null });
    // Clear user notifications upon logout
    useNotificationStore.getState().clear();
  },

  loadInitialAuth: async () => {
    const token = get().token;
    if (!token) return;
    try {
      const res = await fetch('/api/auth/me', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        set({ user: data.user });
        safeLocalStorage.setItem('uibn_user', JSON.stringify(data.user));
      } else {
        get().logout();
      }
    } catch {
      // network fallback
    }
  },

  switchAccount: (account: User) => {
    if (account.token) {
      get().setAuth(account, account.token);
    }
  },
}));
